const OVERFLOW_CARD_SELECTORS = [
    'cbar-card.cbar-filter-card',
    'cbar-card:has(form.cbar-filter-form)',
    'cbar-card:has(form[name="filter_form"])',
    // Transaction list cards host server-side datatables; cbar-card shadow .card
    // defaults to overflow:hidden and clips wide tables on narrow viewports.
    'cbar-card:has(.table-responsive > cbar-datatable)',
    'cbar-card:has(.-responsive > cbar-datatable)',
    'cbar-card:has(.card-content > cbar-datatable)',
    // Record Payment step cards host cbar-datatable pagination outside the scroll
    // shell; cbar-card shadow .card defaults to overflow:hidden and can clip tbody
    // rows and the footer when the card is a flex child.
    '#payor_search_result cbar-card.recordpayment-step-card',
    '#selected_payor_box cbar-card.recordpayment-step-card',
].join(', ');
const PATCHED_ATTR = 'data-cbar-card-overflow-patched';

function patchFilterCardOverflow(card: Element): boolean {
    const host = card as HTMLElement;
    if (host.getAttribute(PATCHED_ATTR) === 'true') {
        return true;
    }

    const innerCard = host.shadowRoot?.querySelector('.card') as HTMLElement | null;
    if (!innerCard) {
        return false;
    }

    // cbar-card shadow DOM defaults to overflow:hidden on .card, which clips
    // in-flow datatable bodies/pagination and absolute dropdowns from filters.
    innerCard.style.overflow = 'visible';
    host.setAttribute(PATCHED_ATTR, 'true');
    return true;
}

function patchFilterCardsIn(root: ParentNode): boolean {
    let allPatched = true;
    const maybeElement = root as Element;
    if (typeof maybeElement.matches === 'function' && maybeElement.matches(OVERFLOW_CARD_SELECTORS)) {
        allPatched = patchFilterCardOverflow(root) && allPatched;
    }

    root.querySelectorAll(OVERFLOW_CARD_SELECTORS).forEach((card) => {
        allPatched = patchFilterCardOverflow(card) && allPatched;
    });

    return allPatched;
}

function cardsNeedOverflowPatch(root: ParentNode): boolean {
    const cards = root.querySelectorAll(OVERFLOW_CARD_SELECTORS);
    if (cards.length === 0) {
        return false;
    }

    for (let i = 0; i < cards.length; i++) {
        if (cards[i].getAttribute(PATCHED_ATTR) !== 'true') {
            return true;
        }
    }

    return false;
}

async function waitForNextFrame(): Promise<void> {
    await new Promise<void>((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => resolve());
            return;
        }
        setTimeout(resolve, 0);
    });
}

/** Patch cards now; retry until shadow roots exist or attempts are exhausted. */
export async function ensureFilterCardOverflow(
    root: ParentNode = document,
    maxAttempts = 24,
): Promise<void> {
    if (typeof customElements !== 'undefined' && typeof customElements.whenDefined === 'function') {
        await customElements.whenDefined('cbar-card');
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        patchFilterCardsIn(root);
        if (!cardsNeedOverflowPatch(root)) {
            return;
        }
        await waitForNextFrame();
    }
}

export function setupFilterCardOverflow(root: ParentNode = document): void {
    patchFilterCardsIn(root);

    if (typeof MutationObserver === 'undefined') {
        return;
    }

    const observerRoot = root instanceof Document ? root.documentElement : root;
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (typeof (node as HTMLElement).matches !== 'function') {
                    return;
                }

                patchFilterCardsIn(node);
            });
        }
    });

    observer.observe(observerRoot, {childList: true, subtree: true});
}

export async function setupFilterCardOverflowWhenReady(root: ParentNode = document): Promise<void> {
    setupFilterCardOverflow(root);
    await ensureFilterCardOverflow(root);
}
